"use client"

import Link from "next/link"
import { useState } from "react"
import { stateLabel } from "../lib/state-copy.ts"
import type { UserView } from "../lib/user-view.ts"
import { EmptyState } from "./empty-state.tsx"
import { FormattedPointAmount, pointValueLabel } from "./point-amount.tsx"
import { PaginationControls } from "./pagination-controls.tsx"

type CommerceViewModel = Extract<UserView, { readonly kind: "commerce" }>
const dateFormatter = new Intl.DateTimeFormat("ko-KR", { dateStyle: "medium" })
const commercePageSize = 10

export const CommerceStatus = ({ status }: { readonly status: string }) => (
  <small className="commerce-status status-chip" data-commerce-status={status}>
    {stateLabel(status)}
  </small>
)

export const CommerceView = ({ view }: { readonly view: CommerceViewModel }) => {
  const vouchers = view.heading === "내 상품권"
  const [currentPage, setCurrentPage] = useState(1)
  const totalPages = Math.max(1, Math.ceil(view.items.length / commercePageSize))
  const pageItems = view.items.slice((currentPage - 1) * commercePageSize, currentPage * commercePageSize)
  if (view.items.length === 0)
    return <EmptyState description={vouchers ? "상품을 교환하면 발급 상태와 유효기간을 이곳에서 확인할 수 있어요." : "상품 교환을 시작하면 처리 상태를 이곳에서 확인할 수 있어요."} illustration={vouchers ? "empty-vouchers" : "empty-history"} title={vouchers ? "아직 받은 상품권이 없어요" : "아직 주문 내역이 없어요"} />
  return (
    <div className="paginated-list-layout" data-commerce-layout={vouchers ? "vouchers" : "orders"}>
      <ul className="commerce-list" data-commerce-list={vouchers ? "vouchers" : "orders"}>
        {pageItems.map((item) => (
          <li key={item.id}>
            <Link aria-label={`${item.label} ${pointValueLabel(item.amount)} ${stateLabel(item.status)}`} href={vouchers ? `/vouchers/${item.id}` : `/orders/${item.id}`}>
              <span className="commerce-copy">
                <strong>{item.label}</strong>
                <time dateTime={item.occurredAt}>{dateFormatter.format(new Date(item.occurredAt))}</time>
                {item.expiresAt === null ? null : <small>유효기간 {dateFormatter.format(new Date(item.expiresAt))}</small>}
              </span>
              <span className="commerce-result">
                <b>
                  <FormattedPointAmount value={item.amount} />
                </b>
                <CommerceStatus status={item.status} />
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <PaginationControls currentPage={currentPage} label={vouchers ? "내 상품권 페이지" : "주문 내역 페이지"} onPageChange={setCurrentPage} totalPages={totalPages} />
    </div>
  )
}
